/**
 * Forgot Password Page Component
 *
 * Allows users to request a password reset link via email.
 * Features a two-state UI: email input form and success confirmation.
 * Styled with the Armenian Link light theme (warm background, amber accents).
 *
 * @module pages/forgot-password
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Loader2, ArrowLeft, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { forgotPassword } from '@/client/lib/auth-client';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@/client/lib/validations';
import { cn } from '@/client/lib/utils';
import { useReducedMotion } from '@/client/hooks/use-reduced-motion';

export function ForgotPasswordPage() {
  const prefersReducedMotion = useReducedMotion();
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordInput) => {
    setIsLoading(true);
    try {
      await forgotPassword(data.email);
      setSubmitted(true);
    } catch (_error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-bold text-xl group">
            <img
              src="/assets/logo.png"
              alt="Armenian Link"
              className="h-8 w-8 rounded-lg group-hover:scale-110 transition-transform"
            />
            <span className="text-foreground">Armenian Link</span>
          </Link>
          <Link
            to="/login"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to login
          </Link>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.5 }}
          className="w-full max-w-md relative"
        >
          {/* Card glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 rounded-2xl blur-lg opacity-60 -z-10" />

          <div className="relative bg-card rounded-2xl p-8 border border-border shadow-lg">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-3">Check your email</h1>
                <p className="text-muted-foreground mb-8">
                  We&apos;ve sent a password reset link to your email address. Click the link to
                  reset your password.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </motion.div>
            ) : (
              <>
                {/* Header */}
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-foreground mb-2">Forgot password?</h1>
                  <p className="text-muted-foreground">
                    Enter your email and we&apos;ll send you a reset link.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      autoComplete="email"
                      inputMode="email"
                      spellCheck={false}
                      {...form.register('email')}
                      className={cn(
                        'w-full h-12 px-4 bg-background border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all',
                        form.formState.errors.email
                          ? 'border-destructive/50 focus:ring-destructive/50'
                          : 'border-border focus:ring-primary/50 focus:border-primary/50'
                      )}
                      placeholder="you@example.com"
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-destructive mt-2">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    whileHover={{ scale: prefersReducedMotion ? 1 : 1.01 }}
                    whileTap={{ scale: prefersReducedMotion ? 1 : 0.99 }}
                    className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-muted disabled:to-muted disabled:cursor-not-allowed text-zinc-900 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send reset link
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </motion.button>
                </form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-border text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to login
                  </Link>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border py-6 bg-background">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Armenian Link. Made with pride.
          </p>
        </div>
      </footer>
    </div>
  );
}
